import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { StructuredApiErrorResponse } from "../types/audit-data.types";

interface ServerErrorDetailsProps {
  serverErrors: StructuredApiErrorResponse | null;
  legacyErrors: string[];
}

export const ServerErrorDetails: React.FC<ServerErrorDetailsProps> = ({
  serverErrors,
  legacyErrors,
}) => {
  return (
    <>
      {/* Structured Server Validation Errors Display */}
      {serverErrors && (
        <Paper
          elevation={2}
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 3,
            border: "1px solid #ffcdd2",
            backgroundColor: "#fff5f5",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ErrorOutlineIcon color="error" fontSize="large" />
            <Box>
              <Typography variant="h6" fontWeight="700" color="error.main">
                Excel Validation Failed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {serverErrors.message || "Please review and correct the errors in the Excel file."}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {serverErrors.details?.map((sheetGroup, sheetIdx) => (
            <Accordion
              key={sheetIdx}
              defaultExpanded
              sx={{
                mb: 2,
                borderRadius: "8px !important",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                border: "1px solid #ef9a9a",
                overflow: "hidden",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: "#ffebee", py: 0.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                  <Typography variant="subtitle1" fontWeight="700" color="error.dark">
                    Sheet: "{sheetGroup.sheet_name}"
                  </Typography>
                  <Chip
                    label={`${sheetGroup.error.length} ${sheetGroup.error.length === 1 ? "Error" : "Errors"}`}
                    color="error"
                    size="small"
                    sx={{ fontWeight: 700, height: 22 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, backgroundColor: "#ffffff" }}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fafafa" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Submitted Input</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Validation Messages</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sheetGroup.error.map((errItem, errIdx) => (
                        <TableRow key={errIdx} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {errItem.excel_field || errItem.field || "N/A"}
                          </TableCell>
                          <TableCell>
                            {errItem.input !== undefined &&
                            errItem.input !== null &&
                            String(errItem.input).trim() !== "" ? (
                              <Chip
                                label={String(errItem.input)}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontFamily: "monospace", fontWeight: 600 }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                            {errItem.expected && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Expected:
                                </Typography>
                                <Chip
                                  label={errItem.expected}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ fontFamily: "monospace", fontWeight: 600 }}
                                />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {errItem.messages && errItem.messages.length > 0 ? (
                              errItem.messages.map((msg, mIdx) => (
                                <Typography key={mIdx} variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                                  • {msg}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body2" color="error.main">
                                Validation failed for field.
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Legacy / Unstructured Errors */}
      {legacyErrors.length > 0 && !serverErrors && (
        <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
          <AlertTitle>Upload Failed</AlertTitle>
          {legacyErrors.map((err, i) => (
            <Typography key={i} variant="body2" display="block">
              • {err}
            </Typography>
          ))}
        </Alert>
      )}
    </>
  );
};
